import React, { useEffect, useRef, useState } from 'react';
import { Draggable, DraggableProvided, Droppable } from '@hello-pangea/dnd';
import { AiManagementCard, AiManagementColumn } from 'store/modules/ai_management_store/types';
import AiManagementKanbanCard from 'components/molecules/ai_management_kanban_card';
import AiManagementAnalyzeEmpty from 'components/molecules/ai_management_analyze_empty';
import { Container } from './styles';

interface AiManagementKanbanColumnProps {
  column: AiManagementColumn;
  cards: AiManagementCard[];
  provided: DraggableProvided;
  isDragging: boolean;
  filtered: boolean;
  onRename: (id: number, name: string) => void;
  onRecolor: (column: AiManagementColumn) => void;
  onDelete: (column: AiManagementColumn) => void;
}

const AiManagementKanbanColumn: React.FC<AiManagementKanbanColumnProps> = ({
  column,
  cards,
  provided,
  isDragging,
  filtered,
  onRename,
  onRecolor,
  onDelete,
}) => {
  const { id, name, color, permissions } = column;
  const { wip_limit: wipLimit, system_key: systemKey, total_cards_count: totalCount } = column;
  const countLabel = wipLimit ? `${cards.length}/${wipLimit}` : String(cards.length);

  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const startEditing = () => {
    setDraftName(name);
    setEditing(true);
    setMenuOpen(false);
  };

  const commitRename = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== name) onRename(id, trimmed);
    setEditing(false);
  };

  const renderEmpty = () => {
    if (filtered && totalCount > 0) {
      return (
        <div className="d-flex align-items-center justify-content-center text-muted small text-center rounded column-empty">
          Nenhuma licitação com os filtros aplicados
        </div>
      );
    }
    if (systemKey === 'analyze') return <AiManagementAnalyzeEmpty teamCount={totalCount} />;
    return (
      <div className="d-flex align-items-center justify-content-center text-muted small rounded column-empty">
        Arraste os cards aqui
      </div>
    );
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') commitRename();
    if (event.key === 'Escape') setEditing(false);
  };

  return (
    <Container
      ref={provided.innerRef}
      barColor={color}
      menuOpen={menuOpen}
      {...provided.draggableProps}
      className={`flex-shrink-0 mr-3 rounded bg-white d-flex flex-column ${
        isDragging ? 'shadow' : ''
      }`}
    >
      <div className="d-flex align-items-center justify-content-between px-3 py-2">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            className="form-control form-control-sm"
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={commitRename}
          />
        ) : (
          <div
            className={`d-flex align-items-center text-truncate ${
              permissions.reorderable ? 'column-drag-handle' : ''
            }`}
            {...(provided.dragHandleProps ?? {})}
          >
            <span className="font-weight-bold text-dark text-truncate">{name}</span>
            <span className="badge badge-light badge-pill border ml-2">{countLabel}</span>
          </div>
        )}

        {!editing && (
          <div className="dropdown" ref={menuRef}>
            <button
              type="button"
              className="btn btn-link btn-sm text-muted p-0 d-flex align-items-center"
              aria-label={`Opções da coluna ${name}`}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <i className="material-icons">more_horiz</i>
            </button>
            {menuOpen && (
              <div className="dropdown-menu dropdown-menu-right show">
                {permissions.renameable && (
                  <button type="button" className="dropdown-item" onClick={startEditing}>
                    Editar nome da coluna
                  </button>
                )}
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    setMenuOpen(false);
                    onRecolor(column);
                  }}
                >
                  Alterar cor da coluna
                </button>
                {permissions.deletable && (
                  <button
                    type="button"
                    className="dropdown-item text-danger"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete(column);
                    }}
                  >
                    Excluir coluna
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <Droppable droppableId={`cards:${id}`} type="CARD">
        {(dropProvided, dropSnapshot) => (
          <div
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
            className="px-3 pb-3 flex-grow-1 overflow-auto"
          >
            {cards.length === 0 && !dropSnapshot.isDraggingOver && renderEmpty()}
            {cards.map((card, index) => (
              <Draggable key={card.id} draggableId={`card:${card.id}`} index={index}>
                {(cardProvided, cardSnapshot) => (
                  <AiManagementKanbanCard
                    card={card}
                    provided={cardProvided}
                    isDragging={cardSnapshot.isDragging}
                  />
                )}
              </Draggable>
            ))}
            {dropProvided.placeholder}
          </div>
        )}
      </Droppable>
    </Container>
  );
};

export default AiManagementKanbanColumn;
